import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import type { GridRowId, GridRowSelectionModel, GridTreeNode } from '@mui/x-data-grid-premium';
import { GRID_ROOT_GROUP_ID } from '@mui/x-data-grid-premium';

/**
 * Minimal structural shape of the MUI grid API this hook drives. Using a structural type
 * instead of the full `GridApi` lets tests pass a small stub without casts, and keeps the
 * hook honest about what it actually depends on.
 */
export interface SelectionPropagationGridApi {
  getRowNode: (id: GridRowId) => GridTreeNode | null;
  getSortedRowIds: () => GridRowId[];
  setRowSelectionModel: (model: GridRowSelectionModel) => void;
  state: { rowSelection: GridRowSelectionModel };
  subscribeEvent: (event: 'rowsSet', handler: () => void) => () => void;
}

/**
 * Selects/deselects a tree row's descendants when the row itself is toggled.
 *
 * MUI's own `rowSelectionPropagation` is dead for the native lazy tree
 * (`groupingName: 'tree-data-source'`) in x-data-grid 9.4 — the data-source tree builder
 * leaves `state.rows.treeDepths` empty, so the grid computes "not nested" and skips
 * propagation entirely. This hook reproduces the `descendants: true, parents: false`
 * behavior by extending each selection-model change. It is idempotent, so it degrades
 * harmlessly if an upstream fix ever activates the built-in propagation — remove it when
 * MUI populates treeDepths for data-source trees.
 */
export function useDescendantSelectionPropagation(
  apiRef: React.RefObject<SelectionPropagationGridApi | null>
) {
  const prevIdsRef = useRef<Set<GridRowId>>(new Set());
  const seenRowIdsRef = useRef<Set<GridRowId>>(new Set());
  /**
   * Rows the user deliberately unchecked under a still-checked ancestor. Kept outside the row
   * tree because it must outlive the row: a filter, sort, or refresh rebuilds the tree from
   * scratch, and the same row coming back must not be re-selected by the propagation below.
   * Cleared as soon as nothing is selected, since exclusions then constrain nothing.
   */
  const excludedIdsRef = useRef<Set<GridRowId>>(new Set());

  /**
   * Children fetched AFTER their parent was checked (check collapsed parent → expand) arrive
   * unselected, because no selection-model change fires for them. Watch rows being added to the
   * tree and select newly loaded rows whose ancestor is selected, skipping the ones the user
   * excluded. Rows already in the tree are left alone — they were settled when their ancestor
   * was checked.
   */
  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;

    return api.subscribeEvent('rowsSet', () => {
      const sortedRowIds = api.getSortedRowIds();
      const newIds = sortedRowIds.filter(id => !seenRowIdsRef.current.has(id));
      seenRowIdsRef.current = new Set(sortedRowIds);
      if (!newIds.length) return;

      const model = api.state.rowSelection;
      if (model.type !== 'include' || model.ids.size === 0) return;

      const excludedIds = excludedIdsRef.current;
      const extendedIds = new Set(model.ids);
      for (const id of newIds) {
        if (excludedIds.has(id)) continue;
        const node = api.getRowNode(id);
        if (!node || node.type === 'skeletonRow') continue;
        const hasSelectedAncestor = getHasSelectedAncestor({
          api,
          excludedIds,
          parentId: node.parent,
          selectedIds: extendedIds,
        });
        if (hasSelectedAncestor) extendedIds.add(id);
      }
      if (extendedIds.size === model.ids.size) return;

      prevIdsRef.current = extendedIds;
      api.setRowSelectionModel({ ids: extendedIds, type: 'include' });
    });
  }, [apiRef]);

  return useCallback(
    (model: GridRowSelectionModel) => {
      const api = apiRef.current;
      if (!api || model.type !== 'include') return;

      const prevIds = prevIdsRef.current;
      const addedIds = [...model.ids].filter(id => !prevIds.has(id));
      const removedIds = [...prevIds].filter(id => !model.ids.has(id));
      prevIdsRef.current = new Set(model.ids);
      if (!addedIds.length && !removedIds.length) return;

      const sortedRowIds = api.getSortedRowIds();
      const excludedIds = excludedIdsRef.current;
      const extendedIds = new Set(model.ids);
      let changed = false;
      for (const id of addedIds) {
        excludedIds.delete(id);
        for (const descendantId of getDescendantIds(api, sortedRowIds, id)) {
          excludedIds.delete(descendantId);
          if (extendedIds.has(descendantId)) continue;
          extendedIds.add(descendantId);
          changed = true;
        }
      }
      for (const id of removedIds) {
        // A row dropped from the model because it left the tree is not a user decision.
        if (api.getRowNode(id)) excludedIds.add(id);
        for (const descendantId of getDescendantIds(api, sortedRowIds, id)) {
          if (!extendedIds.has(descendantId)) continue;
          extendedIds.delete(descendantId);
          changed = true;
        }
      }
      if (extendedIds.size === 0) excludedIds.clear();
      if (!changed) return;

      prevIdsRef.current = extendedIds;
      api.setRowSelectionModel({ ids: extendedIds, type: 'include' });
    },
    [apiRef]
  );
}

/**
 * Descendants = the contiguous deeper-depth run right after the row in sorted order (the same
 * walk MUI's own propagation does), minus lazy-loading skeletons. Children not yet lazily loaded
 * can't be collected — same limitation as the built-in propagation.
 */
function getDescendantIds(
  api: SelectionPropagationGridApi,
  sortedRowIds: GridRowId[],
  rootId: GridRowId
): GridRowId[] {
  const rootNode = api.getRowNode(rootId);
  if (rootNode?.type !== 'group') return [];

  const descendantIds: GridRowId[] = [];
  for (let i = sortedRowIds.indexOf(rootId) + 1; i < sortedRowIds.length; i += 1) {
    const node = api.getRowNode(sortedRowIds[i]);
    if (!node || node.depth <= rootNode.depth) break;
    if (node.type !== 'skeletonRow') descendantIds.push(sortedRowIds[i]);
  }
  return descendantIds;
}

/**
 * An excluded ancestor stops the walk: everything below a row the user unchecked stays out of
 * the selection, even when a higher ancestor is still checked.
 */
function getHasSelectedAncestor({
  api,
  excludedIds,
  parentId,
  selectedIds,
}: {
  api: SelectionPropagationGridApi;
  excludedIds: Set<GridRowId>;
  parentId: GridRowId | null;
  selectedIds: Set<GridRowId>;
}): boolean {
  let ancestorId = parentId;
  while (ancestorId != null && ancestorId !== GRID_ROOT_GROUP_ID) {
    if (excludedIds.has(ancestorId)) return false;
    if (selectedIds.has(ancestorId)) return true;
    ancestorId = api.getRowNode(ancestorId)?.parent ?? null;
  }
  return false;
}
